import type {
  TransitionPresentation,
  TransitionPresentationComponentProps,
} from "@remotion/transitions";
import type { CSSProperties, FC } from "react";
import { useId } from "react";
import { AbsoluteFill, useVideoConfig } from "remotion";

// The outgoing scene splits down the middle and both halves slide out to the
// sides like doors, revealing the incoming scene in the widening gap.
//
// Sliding two halves would normally mean rendering the outgoing scene twice
// (one copy per half), which mounts a second <Video> and stalls the player.
// Instead a single SVG filter crops the one copy into halves and offsets them.
// The incoming scene sits on top and is clipped to the gap, so the doors and
// the opening always meet edge to edge and the frame is never see-through.

export type SplitRevealProps = Record<string, unknown>;

// Below this the doors are effectively shut and the filter is pure overhead.
const MIN_OPEN_PERCENT = 0.01;

// The incoming scene overlaps each door by a pixel to hide antialiasing seams.
const SEAM_OVERLAP_PX = 1;

/** How far each door has slid, in % of the frame width (0 = shut, 50 = gone). */
export function getSplitRevealOpenPercent(presentationProgress: number) {
  return Math.min(50, Math.max(0, presentationProgress * 50));
}

export function getSplitRevealEnteringStyle(
  presentationProgress: number,
): CSSProperties {
  if (presentationProgress >= 1) {
    return {};
  }

  const side = 50 - getSplitRevealOpenPercent(presentationProgress);
  return {
    clipPath: `inset(0 calc(${side}% - ${SEAM_OVERLAP_PX}px) 0 calc(${side}% - ${SEAM_OVERLAP_PX}px))`,
  };
}

const SplitRevealPresentation: FC<
  TransitionPresentationComponentProps<SplitRevealProps>
> = ({ children, presentationProgress, presentationDirection }) => {
  const { width, height } = useVideoConfig();
  const filterId = `split-reveal-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;

  if (presentationDirection === "entering") {
    return (
      <AbsoluteFill style={getSplitRevealEnteringStyle(presentationProgress)}>
        {children}
      </AbsoluteFill>
    );
  }

  const openPercent = getSplitRevealOpenPercent(presentationProgress);
  const half = width / 2;
  const shift = (openPercent / 100) * width;

  return (
    <>
      <svg width={0} height={0} style={{ position: "absolute" }} aria-hidden>
        <defs>
          <filter
            id={filterId}
            x={0}
            y={0}
            width={width}
            height={height}
            filterUnits="userSpaceOnUse"
            primitiveUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feOffset
              in="SourceGraphic"
              x={0}
              y={0}
              width={half}
              height={height}
              result="leftHalf"
            />
            <feOffset
              in="leftHalf"
              dx={-shift}
              x={0}
              y={0}
              width={width}
              height={height}
              result="leftDoor"
            />
            <feOffset
              in="SourceGraphic"
              x={half}
              y={0}
              width={half}
              height={height}
              result="rightHalf"
            />
            <feOffset
              in="rightHalf"
              dx={shift}
              x={0}
              y={0}
              width={width}
              height={height}
              result="rightDoor"
            />
            <feMerge>
              <feMergeNode in="leftDoor" />
              <feMergeNode in="rightDoor" />
            </feMerge>
          </filter>
        </defs>
      </svg>
      <AbsoluteFill
        style={{
          filter:
            openPercent >= MIN_OPEN_PERCENT ? `url(#${filterId})` : undefined,
        }}
      >
        {children}
      </AbsoluteFill>
    </>
  );
};

export function splitReveal(): TransitionPresentation<SplitRevealProps> {
  return { component: SplitRevealPresentation, props: {} };
}
